import { useState, useEffect } from 'react';
import { supabase, checkRateLimit, clearRateLimit } from '../lib/supabase';
import type { Session } from '@supabase/supabase-js';

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'landlord' | 'tenant';
}

// 全域單例狀態，避免多個 useAuth 實例互相干擾
let globalUser: User | null = null;
let globalLoading = true;
let listeners: Array<() => void> = [];

function notifyListeners() {
  listeners.forEach(fn => fn());
}

function mapSessionToUser(session: Session | null): User | null {
  if (!session?.user) return null;
  const u = session.user;
  return {
    id: u.id,
    email: u.email || '',
    name: u.user_metadata?.name || '',
    role: u.user_metadata?.role || 'tenant',
  };
}

// 只初始化一次
let initialized = false;
function initGlobalAuth() {
  if (initialized) return;
  initialized = true;

  supabase.auth.getSession().then(({ data: { session }, error }) => {
    if (error?.message?.includes('Refresh Token') || error?.message?.includes('refresh_token')) {
      supabase.auth.signOut().catch(() => {});
      globalUser = null;
    } else {
      globalUser = mapSessionToUser(session);
    }
    globalLoading = false;
    notifyListeners();
  }).catch((err: unknown) => {
    const msg = err instanceof Error ? err.message : '';
    if (msg.includes('Refresh Token') || msg.includes('refresh_token')) {
      supabase.auth.signOut().catch(() => {});
    }
    globalUser = null;
    globalLoading = false;
    notifyListeners();
  });

  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_OUT') {
      globalUser = null;
    } else {
      globalUser = mapSessionToUser(session);
    }
    globalLoading = false;
    notifyListeners();
  });
}

export function useAuth() {
  initGlobalAuth();

  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const listener = () => forceUpdate(n => n + 1);
    listeners.push(listener);
    return () => {
      listeners = listeners.filter(l => l !== listener);
    };
  }, []);

  const register = async (
    name: string,
    email: string,
    password: string,
    role: 'landlord' | 'tenant'
  ): Promise<{ success: boolean; error?: string; user?: User }> => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { name, role } },
      });
      if (error) return { success: false, error: error.message };
      if (!data.user) return { success: false, error: '註冊失敗，請稍後再試' };
      const newUser: User = {
        id: data.user.id,
        email: data.user.email || '',
        name,
        role,
      };
      return { success: true, user: newUser };
    } catch {
      return { success: false, error: '註冊失敗，請稍後再試' };
    }
  };

  const login = async (
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string; user?: User }> => {
    // ── 速率限制：同一 email 1 分鐘內最多 5 次嘗試 ──
    const rlKey = `login:${email.toLowerCase()}`;
    const rl = checkRateLimit(rlKey, 5, 60_000);
    if (!rl.allowed) {
      const secs = Math.ceil((rl.remainingMs ?? 60_000) / 1000);
      return { success: false, error: `嘗試次數過多，請等待 ${secs} 秒後再試` };
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        if (error.message.includes('Invalid login credentials')) return { success: false, error: 'Email 或密碼錯誤' };
        if (error.message.includes('Email not confirmed')) return { success: false, error: '請先至信箱驗證 Email' };
        return { success: false, error: error.message };
      }
      if (!data.user) return { success: false, error: '登入失敗，請稍後再試' };

      if (!data.user.email_confirmed_at) {
        await supabase.auth.signOut();
        return { success: false, error: '請先至信箱驗證 Email' };
      }

      // 登入成功，清除速率限制
      clearRateLimit(rlKey);

      const loggedInUser = mapSessionToUser(data.session);
      if (!loggedInUser) return { success: false, error: '登入失敗，請稍後再試' };
      return { success: true, user: loggedInUser };
    } catch {
      return { success: false, error: '登入失敗，請稍後再試' };
    }
  };

  const loginWithFacebook = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: `${window.location.origin}/`,
        },
      });
      if (error) return { success: false, error: error.message };
      return { success: true };
    } catch {
      return { success: false, error: 'Facebook 登入失敗，請稍後再試' };
    }
  };

  const loginWithLINE = () => {
    const LINE_CHANNEL_ID = '2009503175';
    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/line/callback`);
    const state = Math.random().toString(36).substring(2);
    const lineAuthUrl = `https://access.line.me/oauth2/v2.1/authorize?response_type=code&client_id=${LINE_CHANNEL_ID}&redirect_uri=${redirectUri}&state=${state}&scope=profile%20openid&bot_prompt=normal`;
    window.location.href = lineAuthUrl;
  };

  const logout = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) return { success: false, error: error.message };
      globalUser = null;
      notifyListeners();
      return { success: true };
    } catch {
      return { success: false, error: '登出失敗，請稍後再試' };
    }
  };

  const resendVerificationEmail = async (
    email: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });
      if (error) return { success: false, error: error.message };
      return { success: true };
    } catch {
      return { success: false, error: '發送失敗，請稍後再試' };
    }
  };

  return {
    user: globalUser,
    login,
    logout,
    register,
    loginWithFacebook,
    loginWithLINE,
    resendVerificationEmail,
    isLoggedIn: !!globalUser,
    loading: globalLoading,
  };
}
